import { ReactNode, useEffect } from 'react';
import { Box, Button, Center, Group, Loader, NavLink, ScrollArea, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconArrowLeft,
  IconBuilding,
  IconChartBar,
  IconKey,
  IconMessageReport,
  IconTimeline,
  IconUpload,
  IconUserCheck,
  IconUserPlus,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { isAdmin } from '~api';
import { api } from '~portal/api';
import { BrandLogo } from '~portal/components/BrandLogo';
import { NavLinkLink } from '~portal/components/common';
import { isStandalone, logger, pageScrollerProps, PageScrollers } from '~portal/lib';
import * as styles from './route.css';

export const Route = createFileRoute('/_protected/admin')({
  component: AdminLayout,
});

type AdminPath =
  | '/admin/upload'
  | '/admin/users'
  | '/admin/users/staff'
  | '/admin/users/subscribers'
  | '/admin/users/organisations'
  | '/admin/users/onboarding'
  | '/admin/invite'
  | '/admin/feedback'
  | '/admin/usage'
  | '/admin/inspector';

type AdminNavLeaf = {
  label: string;
  path: AdminPath;
  icon?: ReactNode;
  /** @desc Hidden below the `md` breakpoint — the upload flow needs a wide viewport. */
  desktopOnly?: boolean;
};

type AdminNavItem = AdminNavLeaf | { label: string; icon: ReactNode; children: AdminNavLeaf[] };

const adminNav: AdminNavItem[] = [
  {
    label: 'Upload',
    path: '/admin/upload',
    icon: <IconUpload size={16} />,
    desktopOnly: true,
  },
  {
    label: 'Users',
    icon: <IconUsers size={16} />,
    children: [
      // Requests leads: it is the section's landing page and the queue of actionable items.
      { label: 'Requests', path: '/admin/users', icon: <IconUserCheck size={16} /> },
      { label: 'Staff', path: '/admin/users/staff', icon: <IconUsersGroup size={16} /> },
      { label: 'Subscribers', path: '/admin/users/subscribers', icon: <IconUsers size={16} /> },
      {
        label: 'Organisations',
        path: '/admin/users/organisations',
        icon: <IconBuilding size={16} />,
      },
      { label: 'Onboarding', path: '/admin/users/onboarding', icon: <IconUserPlus size={16} /> },
    ],
  },
  // Subscriber API-key invites — labelled by what they issue, so the entry is not confusable
  // with the Users page's "Invite internal user" flow.
  { label: 'API Keys', path: '/admin/invite', icon: <IconKey size={16} /> },
  // Thumbs up/down given on agent responses (H2-205): totals and the comment feed.
  { label: 'Feedback', path: '/admin/feedback', icon: <IconMessageReport size={16} /> },
  // Agent activity across the beta per UTC day, from the usage ledger (H2-266).
  { label: 'Usage', path: '/admin/usage', icon: <IconChartBar size={16} /> },
  // Per-turn waterfall of any user's conversation: model/tool calls, timings, tokens.
  { label: 'Inspector', path: '/admin/inspector', icon: <IconTimeline size={16} /> },
];

const leaves = (items: AdminNavItem[]): AdminNavLeaf[] =>
  items.flatMap((item) => ('children' in item ? item.children : [item]));

function AdminLayout() {
  const navigate = useNavigate();
  const profileQuery = api.users.me.useQuery();
  const routerState = useRouterState();

  const currentPath = routerState.location.pathname;
  const isMobile = useMediaQuery('(max-width: 62em)'); // Below 'md' breakpoint (992px)

  // Longest matching path wins, so /admin/users/team activates Team, not All users.
  const activePath = leaves(adminNav)
    .filter((leaf) => currentPath.startsWith(leaf.path))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;

  // Redirect non-admin users once profile loads
  useEffect(() => {
    if (profileQuery.isSuccess && !isAdmin(profileQuery.data)) {
      navigate({ to: '/chat/$conversationId', params: { conversationId: 'new' } }).catch((err) => {
        logger.error('[AdminLayout] Navigation failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    }
  }, [profileQuery.isSuccess, profileQuery.data, navigate]);

  // Show loading state while profile loads
  if (profileQuery.isLoading) {
    return (
      <Center h="100%">
        <Loader size="lg" />
      </Center>
    );
  }

  // Render content only for admin users
  if (!isAdmin(profileQuery.data)) {
    return null;
  }

  // Standalone has no TopNav, so the admin sidebar carries the brand and the way back out.
  const backToAgent = () => {
    navigate({ to: '/chat/$conversationId', params: { conversationId: 'new' } }).catch((err) => {
      logger.error('[AdminLayout] Navigation back to agent failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  const navLeaf = (leaf: AdminNavLeaf, inGroup = false) => (
    <NavLinkLink
      key={leaf.path}
      to={leaf.path}
      // Exact only: the router Link otherwise stamps its own active state on any path PREFIX
      // (/admin/users stays lit on /admin/users/subscribers), fighting the `active` prop.
      activeOptions={{ exact: true }}
      label={leaf.label}
      leftSection={leaf.icon}
      active={activePath === leaf.path}
      className={inGroup ? styles.childLink : styles.navItem}
    />
  );

  if (isMobile) {
    // Below `md` the sidebar becomes a horizontal strip of the flattened destinations.
    return (
      <Stack gap={0} className={styles.adminLayout}>
        <ScrollArea type="never" className={styles.mobileNav}>
          <Group gap={4} px="xs" py={4} wrap="nowrap">
            {isStandalone && (
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconArrowLeft size={16} />}
                onClick={backToAgent}
              >
                Back to agent
              </Button>
            )}
            {leaves(adminNav)
              .filter((leaf) => !leaf.desktopOnly)
              .map((leaf) => navLeaf(leaf))}
          </Group>
        </ScrollArea>
        <Box flex={1} className={styles.adminContent} {...pageScrollerProps(PageScrollers.Admin)}>
          <Outlet />
        </Box>
      </Stack>
    );
  }

  return (
    <Group gap={0} align="stretch" wrap="nowrap" className={styles.adminLayout}>
      <Box component="nav" p="sm" className={styles.sidebar}>
        {isStandalone && (
          <Group justify="space-between" wrap="nowrap" px="sm" pt={4} pb="md" gap="xs">
            <BrandLogo height={28} />
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={14} />}
              onClick={backToAgent}
            >
              Back to agent
            </Button>
          </Group>
        )}
        {adminNav.map((item) =>
          'children' in item ? (
            <NavLink
              key={item.label}
              label={item.label}
              leftSection={item.icon}
              defaultOpened
              childrenOffset={28}
              className={styles.navItem}
            >
              {item.children.map((leaf) => navLeaf(leaf, true))}
            </NavLink>
          ) : (
            navLeaf(item)
          ),
        )}
      </Box>
      <Box flex={1} className={styles.adminContent} {...pageScrollerProps(PageScrollers.Admin)}>
        <Outlet />
      </Box>
    </Group>
  );
}
